import { useRef, useState } from 'react';
import * as Yup from 'yup';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { activeNetworkSelector } from 'redux/selectors';
import { useSelector } from 'react-redux';
import { useCreateSharedListMutation, useUpdateSharedListMutation } from 'services/contacts';
import { defaultFilters } from 'components/organisms/contactsFilters/defaultFilters';
import { parseFiltersData } from 'services/contacts/filters';
import strings from 'strings';
import { trackEvent } from 'helpers/analytics';
import isEqual from 'lodash.isequal';
import { useFailedDomains } from 'features/list';
import { useListTitle } from './useListTitle';
import { useSharedList } from './useSharedList';

export const useCreateList = () => {
  const { listId } = useParams();
  const { state, search } = useLocation();
  const isEdit = Boolean(listId);
  const [step, setStep] = useState(state?.step || (isEdit ? 'contacts:create' : 'initial'));
  const formSchema = Yup.object().shape({
    name: Yup.string().required(),
  });
  const activeNetwork = useSelector(activeNetworkSelector);
  const [formDefaultValues, setFormValues] = useState({
    name: '',
    description: '',
  });
  const formRef = useRef();
  const [createSharedList, { isLoading: isSubmitting, isError }] = useCreateSharedListMutation();

  const [updateSharedList, { isLoading: isUpdatingList }] = useUpdateSharedListMutation();
  const [filters, setFilters] = useState(!isEqual(defaultFilters, state?.filters) ? state?.filters : null);
  const history = useHistory();
  const title = useListTitle({ isEdit, step });
  const { setFailedDomains } = useFailedDomains();
  const blackListedFilters = ['sharedListIds'];

  const { isLoadingList, sharedListData } = useSharedList({
    listId,
    setFormValues,
    setFilters,
  });

  const getEventData = (data) => ({
    collectionId: activeNetwork.id,
    list_name: data.sharedList.name,
    autoUpdate: Boolean(data.sharedList.smartOptions?.includes?.('auto_update')),
    object_type: data?.sharedList?.kind,
    ...(data?.sharedList?.kind === 'companies'
      ? { number_of_companies: data?.sharedList?.domains?.length }
      : undefined),
  });

  const onSubmit = async (values) => {
    const payload = { ...values };

    if (filters) {
      const filteredFilters = Object.keys(filters).reduce((acc, key) => {
        if (!blackListedFilters.includes(key)) {
          acc[key] = filters[key];
        }
        return acc;
      }, {});
      payload.smartListOptions = { filters: parseFiltersData(filteredFilters), enabled: true };
    }

    // If a folderId was provided (via navigation state or query), include it in the create payload
    const folderIdFromQuery = new URLSearchParams(search).get('folderId');
    const rawFolderId = state?.folderId ?? folderIdFromQuery;
    const folderId = rawFolderId != null ? Number(rawFolderId) : null;
    if (folderId) {
      payload.folderId = folderId;
    }

    const { error, data } = await createSharedList(payload);

    if (!error) {
      trackEvent('lists:create_list', getEventData(data));
      history.push(`/networks/${activeNetwork.slug}/list/${data.sharedList.id}/contacts`);
    }

    if (data?.meta && data?.meta?.failedDomains?.length > 0) {
      setFailedDomains(data?.meta?.failedDomains.map((domain) => domain.domain));
    }
  };

  const handleSharedListUpdate = async (values, action = 'disable', smartOptionUpdate = 'smart_options', payload) => {
    const { error, data } = await updateSharedList({
      listId,
      ...values,
    });

    if (!error) {
      trackEvent(`contacts:list:${action}_${smartOptionUpdate}`, payload ?? getEventData(data));
    }

    if (error) {
      toast(strings.genericError, { icon: 'danger', id: 'update-list-error' });
    }
  };

  const updateAutoUpdate = async (selectedFilters, action = 'enable', eventPayload = null) => {
    const payload = {
      smartListOptions: {
        filters: parseFiltersData(selectedFilters),
        enabled: true,
      },
    };

    await handleSharedListUpdate(payload, action, 'smart_options', eventPayload);

    setStep('contacts:create');
  };

  const onAutoUpdateSelected = async (selectedFilters) => {
    if (isEdit) {
      await updateAutoUpdate(selectedFilters);
    }

    setFilters(selectedFilters);

    if (!isEdit) {
      setStep('contacts:create');
    }
  };

  const updateSmartOption = async ({ type, action }) => {
    setFormValues(formRef.current.values);

    if (action === 'disable' && type === 'autoUpdate') {
      if (isEdit) {
        handleSharedListUpdate({
          smartListOptions: {
            filters: parseFiltersData(defaultFilters),
            enabled: false,
          },
        });
      }
      setFilters(null);
    }

    if (action === 'edit' && type === 'autoUpdate') {
      setStep('contacts:autoUpdate');
    }
  };

  const updateListTitle = async (values) => {
    await handleSharedListUpdate(
      {
        listId,
        ...values,
      },
      'edit',
      'list',
    );
  };

  const goBack = () => {
    switch (step) {
      case 'contacts:autoUpdate':
        setStep('contacts:create');
        break;
      default:
        setStep('initial');
    }
  };

  const showGoBack = () => {
    if (step === 'initial' || (isEdit && (step === 'contacts:create' || step === 'companies:create'))) {
      return null;
    }

    return goBack;
  };

  return {
    step,
    setStep: (value) => {
      setStep(value);

      if (formRef?.current?.values) {
        setFormValues(formRef.current.values);
      }
    },
    formSchema,
    activeNetwork,
    formRef,
    createSharedList,
    isSubmitting,
    isLoadingList: isEdit && (!sharedListData || isLoadingList),
    isUpdatingList,
    onSubmit: isEdit ? updateListTitle : onSubmit,
    title,
    onAutoUpdateSelected,
    goBack: showGoBack(),
    updateSmartOption,
    filters,
    setFilters,
    formDefaultValues,
    isEdit,
    contactsCount: sharedListData?.contactsCount ?? 0,
    isError,
    hasAutoUpdateFilters: (!isEdit && Boolean(filters)) || sharedListData?.smartOptions?.includes('auto_update'),
    sharedList: sharedListData,
  };
};
